import { useEffect } from "react";

const VERSION_STORAGE_KEY = "eveness_loaded_server_version";
const RELOAD_VERSION_STORAGE_KEY = "eveness_reload_attempted_version";

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000;

const VersionCheck = () => {
  useEffect(() => {
    let isMounted = true;
    let isChecking = false;

    const fetchServerVersion = async () => {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) {
        throw new Error(
          `Version request failed with status ${response.status}`,
        );
      }

      const data = await response.json();

      return data?.version ? String(data.version) : "";
    };

    const clearBrowserCaches = async () => {
      if (!("caches" in window)) return;

      try {
        const cacheKeys = await window.caches.keys();

        await Promise.all(
          cacheKeys.map((cacheKey) => window.caches.delete(cacheKey)),
        );
      } catch (error) {
        console.error("Failed to clear browser caches:", error);
      }
    };

    const checkVersion = async ({ initialCheck = false } = {}) => {
      if (!isMounted || isChecking) return;

      isChecking = true;

      try {
        const serverVersion = await fetchServerVersion();

        if (!isMounted || !serverVersion) return;

        const storedServerVersion = sessionStorage.getItem(
          VERSION_STORAGE_KEY,
        );

        if (initialCheck || !storedServerVersion) {
          sessionStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
          sessionStorage.removeItem(RELOAD_VERSION_STORAGE_KEY);

          return;
        }

        if (storedServerVersion === serverVersion) {
          return;
        }

        const previouslyAttemptedVersion = sessionStorage.getItem(
          RELOAD_VERSION_STORAGE_KEY,
        );

        if (previouslyAttemptedVersion === serverVersion) {
          return;
        }

        sessionStorage.setItem(
          RELOAD_VERSION_STORAGE_KEY,
          serverVersion,
        );

        sessionStorage.setItem(VERSION_STORAGE_KEY, serverVersion);

        await clearBrowserCaches();

        window.location.reload();
      } catch (error) {
        console.error("Version check failed:", error);
      } finally {
        isChecking = false;
      }
    };

    checkVersion({ initialCheck: true });

    const intervalId = window.setInterval(() => {
      checkVersion();
    }, VERSION_CHECK_INTERVAL);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      isMounted = false;

      window.clearInterval(intervalId);

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, []);

  return null;
};

export default VersionCheck; 